
import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, InputAdornment, IconButton } from '@mui/material';
import { SalesContainer, ContentRow, Column, StyledTextField } from './styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import SalesCard from './SalesCard';
import Cart from './Cart';
import { addSale } from '../../actions/sales';
import SalesImg from '../../assets/salesimage.png';
import SaleSummaryDialog from './SalesSummaryDialog';
import { fetchInventory } from '../../actions/inventory';

const MAX_RESULTS = 10;
const MIN_CHARS = 3;

const ymdUTC = (d) => {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toISOString().slice(0, 10);
};

const isExpired = (expiryDate) => {
  const exp = ymdUTC(expiryDate);
  if (!exp) return false;
  const today = ymdUTC(new Date());
  return exp < today;
};

const expirySortKey = (d) => {
  if (!d) return Number.POSITIVE_INFINITY; // no expiry -> last
  const dt = new Date(d);
  const t = dt.getTime();
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
};


const Sales = () => {
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  // const [hasSelected, setHasSelected] = useState(false);
  const dispatch = useDispatch();
  const [saleDialogOpen, setSaleDialogOpen] = useState(false);
  const latestSale = useSelector((s) => (s.sales.list || [])[0]);
  const [expectedRefNo, setExpectedRefNo] = useState(null);
const [waitingForReceipt, setWaitingForReceipt] = useState(false);

  const items = useSelector((s) => s.inventory.list);

  useEffect(() => {
    console.log("✅ inventory.list length:", items?.length);
    console.log("✅ first item keys:", items?.[0] && Object.keys(items[0]));
    console.log("✅ first item:", items?.[0]);
  }, [items]);

  // fetch inventory if it's not loaded yet
  useEffect(() => {
  dispatch(fetchInventory({}));
}, [dispatch]);


  // helper: get safe searchable strings for both medicines + products (now all via itemId)
  const getSearchParts = (inv) => {
    
    const med = inv?.itemId?.medicineId;
    const itemName =
    inv?.itemId?.name ||          // products
    med?.brandName ||             // medicines
    inv?.productName ||
    inv?.name ||
    "";
    const brandName = med?.brandName || "";
    const genericName = med?.genericName || "";

    // strength likely lives on medicine (if you select it), fallback to inv fields
    const strength = med?.strength || inv?.strength || "";


    const supplier = inv?.supplier || "";
    const batch = inv?.batchNo || "";

    return {
      name: String(itemName).toLowerCase(),
      brand: String(brandName).toLowerCase(),
      generic: String(genericName).toLowerCase(),
      strength: String(strength).toLowerCase(),
      supplier: String(supplier).toLowerCase(),
      batch: String(batch).toLowerCase(),
    };
  };

  // -------- SEARCH / FILTER LOGIC --------
  const filteredItems = useMemo(() => {
    const qRaw = query.trim().toLowerCase();
    if (!qRaw || qRaw.length < MIN_CHARS) return [];

    const tokens = qRaw.split(/\s+/).filter(Boolean);
    if (!tokens.length) return [];

    const scored = [];

    for (const item of (items || [])) {
      const { name, brand, generic, strength, supplier, batch } = getSearchParts(item);

      let totalScore = 0;
      let allTokensMatch = true;

      for (const token of tokens) {
        let tokenMatched = false;
        let tokenScore = 0;

        // strongest: item name / brand / generic startsWith
        if (
          name.startsWith(token) ||
          brand.startsWith(token) ||
          generic.startsWith(token)
        ) {
          tokenMatched = true;
          tokenScore = 4;

        // next: item name / brand / generic includes
        } else if (
          name.includes(token) ||
          brand.includes(token) ||
          generic.includes(token)
        ) {
          tokenMatched = true;
          tokenScore = 3;

        // then /strength/batch/supplier startsWith
        } else if (
          strength.startsWith(token) ||
          batch.startsWith(token) ||
          supplier.startsWith(token)
        ) {
          tokenMatched = true;
          tokenScore = 2;

        // then strength/batch/supplier includes
        } else if (
          strength.includes(token) ||
          batch.includes(token) ||
          supplier.includes(token)
        ) {
          tokenMatched = true;
          tokenScore = 1;
        }

        if (!tokenMatched) {
          allTokensMatch = false;
          break;
        }

        totalScore += tokenScore;
      }

      if (!allTokensMatch) continue;

      scored.push({ item, score: totalScore });
    }

    scored.sort((a, b) => {
  // 1) primary: search relevance score
  if (b.score !== a.score) return b.score - a.score;

  // 2) expired goes to bottom (within same score)
  const aExpired = isExpired(a.item?.expiryDate);
  const bExpired = isExpired(b.item?.expiryDate);
  if (aExpired !== bExpired) return aExpired ? 1 : -1;

  // 3) FEFO: soonest expiry first
  const aKey = expirySortKey(a.item?.expiryDate);
  const bKey = expirySortKey(b.item?.expiryDate);
  if (aKey !== bKey) return aKey - bKey;

  // 4) stable fallback (optional): batchNo
  return String(a.item?.batchNo ?? "").localeCompare(String(b.item?.batchNo ?? ""));
});

    return scored.slice(0, MAX_RESULTS).map((entry) => entry.item);
  }, [items, query]);

  const handleClear = () => {
    setQuery('');
    // setHasSelected(false);
    setShowResults(false);
  };

  // -------- CART LOGIC --------
  const handleAddToCart = (item) => {
    if (waitingForReceipt) return;
    if (isExpired(item?.expiryDate)) {
  console.log("Expired batch, blocked:", item?.batchNo);
  return;
}

    const stock = Number(item.quantity ?? 0);

    // 🔒 If item has 0 stock, do NOT add it.
    if (stock <= 0) {
      console.log("Out of stock");
      return;
    }

    setCartItems((prev) => {
      const existingIndex = prev.findIndex(
        (cartItem) =>
          cartItem._id === item._id &&
          cartItem.batchNo === item.batchNo
      );

      // 🟦 CASE 1: Item already in cart
      if (existingIndex !== -1) {
        const existingItem = prev[existingIndex];
        const currentQty = Number(existingItem.qty || 1);
        const newQty = currentQty + 1;

        // 🔒 Do not exceed stock
        if (newQty > stock) {
          console.log(`Reached available stock (${stock}).`);
          return prev; // do nothing
        }

        // update the qty
        return prev.map((cartItem, i) =>
          i === existingIndex
            ? { ...cartItem, qty: newQty }
            : cartItem
        );
      }

      // 🟩 CASE 2: First time adding this item
      return [
        ...prev,
        {
          ...item,
          qty: 1,
        },
      ];
    });
  };

  const handleUpdateQty = (item, newQty, extra = {}) => {
    setCartItems((prev) =>
      prev.map((cartItem) =>
        cartItem._id === item._id && cartItem.batchNo === item.batchNo
          ? { ...cartItem, qty: newQty, ...extra }
          : cartItem
      )
    );
  };

  const handleRemoveItem = (item) => {
    setCartItems((prev) =>
      prev.filter(
        (cartItem) =>
          !(
            cartItem._id === item._id &&
            cartItem.batchNo === item.batchNo
          )
      )
    );
  };

  const handleCompleteSale = (payload) => {
    console.log("📤 Sending payload to backend:", payload);
    setExpectedRefNo(payload.refNo);     // ✅ we know what sale should arrive
    setWaitingForReceipt(true); 
    dispatch(addSale(payload));
    setSaleDialogOpen(true);
  };

  const safeSale =
  !waitingForReceipt
    ? latestSale
    : (latestSale?.refNo === expectedRefNo ? latestSale : null);

    useEffect(() => {
  if (waitingForReceipt && latestSale?.refNo === expectedRefNo) {
    setWaitingForReceipt(false);
  }
}, [waitingForReceipt, latestSale, expectedRefNo]);

  useEffect(() => {
    if (
      !waitingForReceipt &&
      expectedRefNo &&
      latestSale?.refNo === expectedRefNo
    ) {
      // 🧹 reset UI
      setCartItems([]);
      setQuery('');
      setShowResults(false);

      // get fresh quantities from backend
      dispatch(fetchInventory({}));
    }
  }, [waitingForReceipt, expectedRefNo, latestSale, dispatch]);



  // -------- RENDER --------
  return (
    <SalesContainer>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '90%',
          margin: 'auto',
          py: '15px',
          pb: '32px',

          // ✅ add breathing room on small screens
    px: { xs: 1, sm: 0 },

    // ✅ extra padding specifically for landscape mobile
    '@media (orientation: landscape) and (max-width: 900px)': {
      px: 3,
    },
        }}
      >
        <Typography variant="h3" sx={{ fontSize: {
      xs: '1.6rem',  // mobile
      sm: '2rem',    // small tablets
      md: '2.5rem',  // desktop
    },  fontWeight: 600 }}>
          Sales
        </Typography>
      </Box>

      <Box sx={{ width: '90%', margin: 'auto', 
      '@media (orientation: landscape) and (max-width: 900px)': {
      marginLeft: '8%',
      width: '90%',
    },
     }}>
      <Typography variant="body2" sx={{textAlign:'left', marginLeft:'10px', fontSize:'14px'}}>Start typing to search your inventory and add items to the cart.</Typography>
        <ContentRow style={{ paddingTop: '10px' }}>
          <Column style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
            <div style={{ width: '100%' }}>
              <StyledTextField
              autoComplete="off"
                sx={{
                  '& .MuiOutlinedInput-root': { '& fieldset': { border: 'none' } },
                  '& .MuiOutlinedInput-input::placeholder': {
      fontSize: {
        xs: '0.85rem',  // 📱 mobile
        sm: '0.95rem',  // tablet
        md: '1rem',     // desktop
      }},
                }}
                variant="outlined"
                placeholder="Search Inventory"
                value={query}
                onChange={(e) => {
                  const value = e.target.value;
                  setQuery(value);
                  // setHasSelected(false);
                  const trimmed = value.trim();
                  if (trimmed.length >= MIN_CHARS) {
                    setShowResults(true);
                  } else {
                    setShowResults(false);
                  }
                }}
                slotProps={{
                  input: {
                    style: {
                      color: '#333',
                      fontSize: '1rem',
                      fontWeight: '500',
                      fontFamily: 'Roboto',
                      background: '#f3f3f6',
                      border: '0 !important',
                    },
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: query && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={handleClear}
                          sx={{
                            padding: 0,
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            background: 'white',
                            color: '#333',
                          }}
                        >
                          <CloseIcon style={{ fontSize: '16px' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </div>

            <div style={{ width: '100%', paddingTop: '25px' }}>
              {showResults ? (
                filteredItems?.map((item) => (
                  <div key={item._id}>
                    <SalesCard item={item} addToCart={handleAddToCart} cartItems={cartItems} />
                  </div>
                ))
              ) : (
                <Box
  component="img"
  src={SalesImg}
  alt="Search illustration"
  sx={{
    height: { xs: '40vh', sm: '50vh' },
    width: { xs: '100%', sm: 'auto' },
    maxWidth: { xs: '100%', sm: 'none' },
    mt: { xs: '16px', sm: '25px' },
    mb: '16px',
    objectFit: 'contain',
  }}
/>
              )}
            </div>
          </Column>

          <Column>
            <Cart
              cartItems={cartItems}
              onUpdateQty={handleUpdateQty}
              onRemoveItem={handleRemoveItem}
              onCompleteSale={handleCompleteSale}
            />
          </Column>
        </ContentRow>
      </Box>

      {latestSale && (
        <SaleSummaryDialog
          open={saleDialogOpen}
          onClose={() => setSaleDialogOpen(false)}
          sale={safeSale}
        />
      )}
    </SalesContainer>
  );
};

export default Sales;
